import React, {Component, PureComponent} from 'react';
import { NavLink } from 'react-router-dom';
import {aspect} from 'utils/ImageTools';
import xClass from 'utils/xClass';

export function buildPictureGroups(
	pictures,
	containerWidth,
	{minHeight, extraWidth, shouldLoad}
){
	if (!minHeight){
		minHeight = 150;
	}
	// split up sliced into rows, each with a height to scale
	let groups = [{
		pxWidth: 0,
		images: [],
		height: minHeight,
		extraWidth: 0,
		multiplier: 1,
		done: false
	}];
	let currGroup = 0;
	let totalHeight = 0;

	for (let pic of pictures){
		if (typeof shouldLoad !== 'function' || shouldLoad(pic.id)){
			let curr = groups[currGroup];
			let computedWidth = aspect(pic.aspect, undefined, minHeight);
			let widthWithNextPic = curr.pxWidth
				 + computedWidth
				 + curr.extraWidth
				 + extraWidth;
				 
			if (widthWithNextPic >= containerWidth){
				let {extraWidth, pxWidth} = curr;
				curr.multiplier = (containerWidth - extraWidth) / pxWidth;
				curr.height = curr.multiplier * minHeight;
				curr.done = true;
				totalHeight += curr.height;
				currGroup++;
				groups[currGroup] = {
					pxWidth: 0,
					images: [],
					height: minHeight,
					extraWidth: 0,
					multiplier: 1,
					done: false
				};
			}
			groups[currGroup].pxWidth += computedWidth;
			groups[currGroup].extraWidth += extraWidth;
			groups[currGroup].images.push(pic);
		}
	}
	return groups;
}

export default class PictureList extends Component{

	static defaultProps = {
		// any additional padding/margins/borders to be added
		extraWidth: 0,
		extraHeight: 0,
		thumbHeightMinimum: 150,
		srcKey: 'thumbnail',
		maxPics: 100,
		useClears: true,
		waitForLoad: false,
		cacheStyles: false
	}

	constructor(props){
		super(props);
		this.state = {loadedImages: []};
		this.resized = this.resized.bind(this);
		this.loadImages = this.loadImages.bind(this);
		this.styleCache = {};
	}

	componentDidMount(){
		window.addEventListener('resize', this.resized);
		this.resized();
	}

	componentWillReceiveProps(){
		if (this.state.containerDiv){
			this.loadImages();
		}
	}

	componentDidUpdate(){
		this.resized();
	}

	resized(){
		let {containerDiv} = this.state;
		if (containerDiv){
			let containerWidth = containerDiv.clientWidth;
			if (containerWidth !== this.state.containerWidth){
				this.setState({
					containerWidth: containerDiv.clientWidth
				});
			}
		}
	}

	componentWillUnmount(){
		window.removeEventListener('resize', this.resized);
	}

	picturesRef = (div) => {
		if (div){
			this.setState({
				containerDiv: div,
				containerWidth: div.clientWidth
			}, () => {
				this.loadImages();
			});
		}
	}

	// images are loaded
	ready = () => {
		this.resized();

		let {
			setWorkDimensions
		} = this.props;
		if (typeof setWorkDimensions === 'function'){
			for (let id in this.imageData){
				let img = this.imageData[id];
				setWorkDimensions(id, {
					width: img.width,
					height: img.height
				});
			}
		}
	}

	loadImages(){
		let {
			extraWidth,
			extraHeight,
			pictureRoot,
			setWorkDimensions,
			srcKey
		} = this.props;
		const self = this;

		let pictures = this.visiblePics();

		if (!this.imageData){
			this.imageData = {};
		}

		let totalPictures = pictures
			.filter(p => p.type =='image')
			.length;

		if (pictures.length){
			let totalLoaded = 0;
			pictures.forEach((img) => {
				if (this.imageData[img.id]){
					return;
				}
				this.imageData[img.id] = {
					loaded: false,
					id: img.id,
					meta: img,
					img: new Image(),
				};
				if (img.aspect){
					this.imageData[img.id].aspect = img.aspect;
				}
				this.imageData[img.id].img.onload = function(){
					let elementWidth = this.naturalWidth;
					let elementHeight = this.naturalHeight;
					// self.resized();
					self.imageData[img.id].width = elementWidth;
					self.imageData[img.id].height = elementHeight;
					self.imageData[img.id].loaded = true;
					// was deferred
					totalLoaded++;
					self.setState(state => ({
						loadedImages: state.loadedImages.concat([img.id]),
						containerWidth: state.containerDiv.clientWidth
					}));
					if (totalLoaded == totalPictures){
						self.ready();
					}
				}
				let src = `${pictureRoot}${img[srcKey]}`;
				this.imageData[img.id].img.src = src;
			})
		}

	}

	makeStyle = ({image, idx, row, firstOnLine, lastOnLine}) => {
		let {
			styleFunc,
			cacheStyles
		} = this.props;
		let styleArgs = {
			image,
			idx,
			row,
			firstOnLine,
			lastOnLine
		}
		if (cacheStyles){
			// caching the styles by id in this.styleCache
			if (!this.styleCache[image.id]){
				this.styleCache[image.id] = styleFunc(styleArgs);
			}
			return this.styleCache[image.id];
		}
		else{
			return styleFunc(styleArgs);
		}
	}

	visiblePics = () => {
		let {pictures, maxPics} = this.props;
		let visible = pictures.filter(p => p.type == 'image');
		return visible.slice(0, maxPics);
	}

	render(){
		let {
			pictureRoot,
			thumbHeightMinimum,
			onClick,
			onMouseEnter,
			onMouseLeave,
			maxPics,
			extraWidth,
			picElement,
			waitForLoad,
			srcKey,
			useClears,
			styleFunc
		} = this.props;

		let pictures = this.visiblePics();

		let {containerWidth} = this.state;

		let imageData = this.imageData;

		return (
			<div
				className="picture-list"
				ref={this.picturesRef}
			>{
				buildPictureGroups(
					this.visiblePics(),
					containerWidth,
					{
						minHeight: thumbHeightMinimum,
						extraWidth,
						shouldLoad: (id) => {
							let img = imageData ? imageData[id] : null;
							return !waitForLoad ||
								(waitForLoad && img && img.loaded);
						}
					}
				).map((g, rowIdx) => {
					let lastIdxOnLine = g.images.length - 1;
					let imgsOnLine = g.images.map((image, imgIdx) => 
						typeof picElement === 'function'
						? picElement(image, g)
						: <PictureListItem
							id={image.id}
							key={image.id}
							image={image}
							height={`${g.height}`}
							width={`${image.aspect * g.height}`}
							onClick={onClick}
							onMouseEnter={onMouseEnter}
							onMouseLeave={onMouseLeave}
							useClears={useClears}
							imgIdx={imgIdx}
							src={`${
								!waitForLoad
								? `${pictureRoot}${image[srcKey]}`
								: undefined
							}`}
							style={useClears ? {
								clear: imgIdx === 0 ? 'right' : 'none'
							} : undefined}
							styleFunc={styleFunc}
							imgStyle={
								Object.assign(
									{},
									styleFunc
									? this.makeStyle({
										image,
										idx: rowIdx + imgIdx,
										row: rowIdx,
										firstOnLine: imgIdx === 0,
										lastOnLine: imgIdx === lastIdxOnLine
									})
									: {}
								)
							}
						/>
					)
					return useClears ? imgsOnLine : (
						<div
							key={`row-${rowIdx}`}
							className="pictures-row"
						>{imgsOnLine}</div>
					);
					
				})
			}</div>
		);

	}	
}

export class PictureListItem extends Component{
	constructor(props){
		super(props);
	}

	shouldComponentUpdate(nextProps, nextState){
		if (!this.props) return true;
		let {id, width, height, style} = this.props;
		if (nextProps.id !== id){
			return true;
		}
		if (nextProps.width !== width
			|| nextProps.height !== height){
			return true;
		}
		if (!style){
			return true;
		}
		if (nextProps.style.clear !== style.clear){
			return true;
		}
		return false;
	}

	onClick = () => {
		let {
			image,
			onClick
		} = this.props;
		if (typeof onClick === 'function'){
			onClick(image);
		}
	}
	onMouseEnter = () => {
		let {
			image,
			onMouseEnter
		} = this.props;
		if (typeof onMouseEnter === 'function'){
			onMouseEnter(image);
		}
	}
	onMouseLeave = () => {
		let {
			image,
			onMouseLeave
		} = this.props;
		if (typeof onMouseLeave === 'function'){
			onMouseLeave(image);
		}
	}
	render(){
		let {
			id,
			image,
			width,
			height,
			style,
			imgStyle,
			src,
			onClick
		} = this.props;
		return (
			<NavLink
				to={`/gallery/${id}`}
				onMouseEnter={this.onMouseEnter}
				onMouseLeave={this.onMouseLeave}
				style={style}
				data-id={id}
			>
				<img
					src={src}
					width={width}
					height={height}
					style={imgStyle}
					data-id={id}
				/>
			</NavLink>
		)
/* 		return (
			<a
				onClick={this.onClick}
				onMouseEnter={this.onMouseEnter}
				onMouseLeave={this.onMouseLeave}
				style={style}
				data-id={id}
				key={id}
			>
				<img
					src={src}
					width={width}
					height={height}
					style={imgStyle}
					data-id={id}
				/>
			</a>
		) */
	}
}